// index.jsx
import React from "react"
import { createRoot } from "react-dom/client"
import ButtonOptionsMenu from "./options-menu"
import HorizontalButtonOptionsMenu from "./horizontal-options-menu"
import ButtonTransformGizmo, { removeExistingGizmo } from "./transform-gizmo"
import ButtonTextDialog from "./text-dialog"
import ButtonStyleDialog from "./style-dialog"
import ButtonSourceDialog from "./source-dialog"
import { twMerge } from "tailwind-merge"
import { HtmlEditorCommander } from "aldoo-ra/Commander"

const { runCommand } = HtmlEditorCommander

const DEFAULT_BUTTON_CONFIG = {
  stylePresets: {
    primary:
      "bg-admin_primary hover:bg-admin_primary_dk text-white px-6 py-2 rounded-lg shadow-sm",
    secondary:
      "bg-admin_neutral_2 hover:bg-admin_neutral_3 text-white px-6 py-2 rounded-lg shadow-sm",
    outline:
      "border-2 border-admin_primary hover:bg-admin_primary/10 text-admin_primary px-6 py-2 rounded-lg",
    ghost: "hover:bg-admin_neutral_1/50 text-admin_text_secondary px-6 py-2 rounded-lg",
    gradient:
      "bg-gradient-to-r from-admin_primary to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white px-6 py-2 rounded-lg shadow-sm",
  },
  alignments: {
    left: "float-left mr-4",
    center: "mx-auto block text-center",
    right: "float-right ml-4",
  },
}

const createButtonElement = (text = "Click me", options = {}) => {
  const button = document.createElement("a")
  button.href = options.href || "#"
  button.className = twMerge(
    "editor-button inline-flex items-center justify-center",
    options.stylePreset || DEFAULT_BUTTON_CONFIG.stylePresets.primary
  )
  button.textContent = text
  button.contentEditable = false

  if (options.alignment) {
    const alignmentClass = DEFAULT_BUTTON_CONFIG.alignments[options.alignment]
    const wrapper = document.createElement("div")
    wrapper.className = twMerge("editor-button-wrapper", alignmentClass)
    wrapper.contentEditable = false
    wrapper.appendChild(button)
    return wrapper
  }

  return button
}

const insertButton = (buttonElement, selectionRange, setSelectionRange) => {
  if (!selectionRange) {
    const editor = document.querySelector(".editor")
    editor.appendChild(buttonElement)
    const range = document.createRange()
    range.setStartAfter(buttonElement)
    range.setEndAfter(buttonElement)
    const selection = window.getSelection()
    selection.removeAllRanges()
    selection.addRange(range)
    setSelectionRange(range)
    return
  }

  const range = selectionRange.cloneRange()
  range.deleteContents()
  range.insertNode(buttonElement)
  range.setStartAfter(buttonElement)
  range.setEndAfter(buttonElement)
  const selection = window.getSelection()
  selection.removeAllRanges()
  selection.addRange(range)
  setSelectionRange(range)
}

const addButton = async (data) => {
  const { selectionRange, setSelectionRange, options = {} } = data

  // Get selected text if there is any
  let buttonText = "Click me"
  if (selectionRange && !selectionRange.collapsed) {
    buttonText = selectionRange.toString().trim()
  }

  // Use selected text or fallback to options.text or default
  const text = buttonText || options.text || "Click me"
  const button = createButtonElement(text, options)
  insertButton(button, selectionRange, setSelectionRange)
  return button
}

const updateButtonStyle = (data) => {
  const { selectedButton, stylePreset, style } = data
  if (!selectedButton) return

  // Use style or stylePreset (for backward compatibility)
  const styleName = style || stylePreset
  if (!styleName) return

  const presetClass = DEFAULT_BUTTON_CONFIG.stylePresets[styleName]
  if (!presetClass) return

  // Remove existing style preset classes
  Object.values(DEFAULT_BUTTON_CONFIG.stylePresets).forEach((className) => {
    const classes = className.split(" ")
    classes.forEach((cls) => selectedButton.classList.remove(cls))
  })
  // Add new preset classes
  presetClass.split(" ").forEach((cls) => selectedButton.classList.add(cls))
}

const updateButtonAlignment = (data) => {
  const { selectedButton, alignment } = data

  if (!selectedButton) return

  const button = selectedButton.classList.contains("editor-button")
    ? selectedButton
    : selectedButton.querySelector(".editor-button")

  // Check if button is in a wrapper or standalone
  let targetElement = button
  const existingWrapper = button.closest('.editor-button-wrapper')

  // For center alignment, we need to ensure proper structure
  if (alignment === 'center') {
    if (!existingWrapper) {
      // Create wrapper for center alignment
      const wrapper = document.createElement("div")
      wrapper.className = "editor-button-wrapper"
      button.parentNode.insertBefore(wrapper, button)
      wrapper.appendChild(button)
      targetElement = wrapper
    } else {
      targetElement = existingWrapper
    }
  } else {
    // For left/right alignment, use existing wrapper or button
    targetElement = existingWrapper || button
  }

  // Remove existing alignment classes from both wrapper and button
  const elementsToClean = [targetElement, button]
  elementsToClean.forEach(el => {
    Object.values(DEFAULT_BUTTON_CONFIG.alignments).forEach((className) => {
      const classes = className.split(" ")
      classes.forEach((cls) => el.classList.remove(cls))
    })
  })

  // Add new alignment classes
  const alignmentClass = DEFAULT_BUTTON_CONFIG.alignments[alignment]
  if (alignmentClass) {
    alignmentClass.split(" ").forEach((cls) => targetElement.classList.add(cls))
  }

  // Update transform gizmo position after alignment change
  setTimeout(() => {
    const gizmo = document.querySelector(".ae-button-transform-gizmo")
    if (gizmo) {
      // Call the update function directly by recreating the logic
      const editorContainer = document.querySelector(".editor")
      if (editorContainer) {
        const editorRect = editorContainer.getBoundingClientRect()
        const buttonRect = selectedButton.getBoundingClientRect()

        // Calculate position relative to the editor container
        const top = buttonRect.top - editorRect.top + editorContainer.scrollTop
        const left = buttonRect.left - editorRect.left + editorContainer.scrollLeft

        gizmo.style.left = `${left}px`
        gizmo.style.top = `${top}px`
        gizmo.style.width = `${buttonRect.width}px`
        gizmo.style.height = `${buttonRect.height}px`
      }
    }
  }, 50) // Small delay to allow DOM changes to apply
}

const updateButtonText = (data) => {
  const { selectedButton, text } = data
  if (!selectedButton) return

  const button = selectedButton.classList.contains("editor-button")
    ? selectedButton
    : selectedButton.querySelector(".editor-button")
  button.textContent = text
}

const updateButtonLink = (data) => {
  const { selectedButton, href } = data
  if (!selectedButton) return

  const button = selectedButton.classList.contains("editor-button")
    ? selectedButton
    : selectedButton.querySelector(".editor-button")
  button.href = href
}

const deleteButton = (data) => {
  const { selectedButton, onDeleteComplete } = data
  if (!selectedButton) return

  // Remove the transform gizmo first
  removeExistingGizmo()

  const wrapper = selectedButton.classList.contains("editor-button-wrapper")
    ? selectedButton
    : selectedButton.closest(".editor-button-wrapper")
  wrapper ? wrapper.remove() : selectedButton.remove()

  // Call onDeleteComplete callback if provided
  if (onDeleteComplete) {
    onDeleteComplete()
  }
}

const updateButtonBackground = (data) => {
  const { selectedButton, imageUrl } = data
  if (!selectedButton) return

  const button = selectedButton.classList.contains("editor-button")
    ? selectedButton
    : selectedButton.querySelector(".editor-button")

  // Add background image and necessary styles
  button.style.backgroundImage = `url(${imageUrl})`
  button.style.backgroundSize = "cover"
  button.style.backgroundPosition = "center"
  button.style.backgroundRepeat = "no-repeat"

  // Add a semi-transparent overlay to ensure text remains readable
  button.style.position = "relative"
  button.style.zIndex = "1"

  // Remove any existing overlay
  const existingOverlay = button.querySelector(".button-overlay")
  if (existingOverlay) {
    existingOverlay.remove()
  }

  // Create overlay element
  const overlay = document.createElement("div")
  overlay.className = "button-overlay absolute inset-0 opacity-50 -z-[1]"

  // Set overlay color based on button style
  if (button.classList.contains("text-white")) {
    overlay.style.backgroundColor = "rgba(0, 0, 0, 0.4)"
  } else {
    overlay.style.backgroundColor = "rgba(255, 255, 255, 0.8)"
  }

  button.insertBefore(overlay, button.firstChild)
}

const removeButtonBackground = (data) => {
  const { selectedButton } = data
  if (!selectedButton) return

  const button = selectedButton.classList.contains("editor-button")
    ? selectedButton
    : selectedButton.querySelector(".editor-button")

  // Remove background image and related styles
  button.style.backgroundImage = ""
  button.style.backgroundSize = ""
  button.style.backgroundPosition = ""
  button.style.backgroundRepeat = ""

  // Remove overlay if it exists
  const overlay = button.querySelector(".button-overlay")
  if (overlay) {
    overlay.remove()
  }
}

const duplicateButton = (data) => {
  const { selectedButton, onDuplicateComplete } = data
  if (!selectedButton) return

  // Handle both wrapped and unwrapped buttons
  let elementToDuplicate = selectedButton
  let parentContainer = selectedButton.parentNode

  // If button is in a wrapper, duplicate the wrapper instead
  const wrapper = selectedButton.closest('.editor-button-wrapper')
  if (wrapper) {
    elementToDuplicate = wrapper
    parentContainer = wrapper.parentNode
  }

  // Clone the element (wrapper or button)
  const clonedElement = elementToDuplicate.cloneNode(true)

  // Generate unique identifiers for the new button to avoid conflicts
  const timestamp = Date.now()
  const randomId = Math.random().toString(36).substr(2, 9)

  // Find the actual button in the cloned element and add unique identifier
  const clonedButton = clonedElement.classList?.contains('editor-button')
    ? clonedElement
    : clonedElement.querySelector('.editor-button')

  if (clonedButton) {
    // Add unique identifier to prevent deletion conflicts
    clonedButton.setAttribute('data-button-id', `btn-${timestamp}-${randomId}`)

    // Remove any inline event handlers that might have been cloned
    clonedButton.onclick = null
    clonedButton.onmousedown = null
    clonedButton.onmouseup = null
  }

  // Insert the cloned element right after the original
  if (!parentContainer) {
    console.error('Cannot duplicate button: parent container not found')
    return null
  }

  try {
    parentContainer.insertBefore(clonedElement, elementToDuplicate.nextSibling)
  } catch (error) {
    console.error('Error inserting duplicated button:', error)
    return null
  }

  // Close current menus and gizmos
  const existingGizmo = document.querySelector(".ae-button-transform-gizmo")
  if (existingGizmo) {
    existingGizmo.remove()
  }
  const existingMenu = document.querySelector(".ae-horizontal-button-options-menu-container")
  if (existingMenu) {
    existingMenu.remove()
  }

  // Get the final button element to focus on
  const finalButton = clonedElement.classList?.contains('editor-button')
    ? clonedElement
    : clonedElement.querySelector('.editor-button')

  // Show gizmo and menu for the new button after a brief delay
  setTimeout(() => {
    if (finalButton) {
      // Create transform gizmo for new button
      const gizmo = document.querySelector(".ae-button-transform-gizmo")
      if (!gizmo) {
        runCommand("button.transformGizmo", { selectedButton: finalButton })
      }

      // Create horizontal menu for new button
      runCommand("button.horizontalMenu", {
        selectedButton: finalButton,
        variables: data.variables || {},
        onButtonUpdated: onDuplicateComplete
      })
    }
  }, 100)

  if (onDuplicateComplete) {
    onDuplicateComplete(finalButton)
  }

  return finalButton
}

const updateButtonSize = (data) => {
  const { selectedButton, size } = data
  if (!selectedButton) return

  const button = selectedButton.classList.contains("editor-button")
    ? selectedButton
    : selectedButton.querySelector(".editor-button")

  // Remove existing size classes
  const sizeClasses = ['btn-sm', 'btn-md', 'btn-lg', 'btn-xl', 'text-xs', 'text-sm', 'text-base', 'text-lg', 'text-xl', 'px-2', 'px-3', 'px-4', 'px-6', 'px-8', 'py-1', 'py-2', 'py-3', 'py-4']
  sizeClasses.forEach(cls => button.classList.remove(cls))

  // Apply size-specific classes
  switch (size) {
    case 'sm':
      button.classList.add('px-3', 'py-1', 'text-sm')
      break
    case 'md':
      button.classList.add('px-4', 'py-2', 'text-base')
      break
    case 'lg':
      button.classList.add('px-6', 'py-3', 'text-lg')
      break
    case 'xl':
      button.classList.add('px-8', 'py-4', 'text-xl')
      break
  }
}

const updateButtonVariant = (data) => {
  const { selectedButton, variant } = data
  if (!selectedButton) return

  const button = selectedButton.classList.contains("editor-button")
    ? selectedButton
    : selectedButton.querySelector(".editor-button")

  // Remove existing variant classes
  const variantClasses = ['rounded', 'rounded-lg', 'rounded-xl', 'rounded-full', 'rounded-none']
  variantClasses.forEach(cls => button.classList.remove(cls))

  // Apply variant-specific classes
  switch (variant) {
    case 'rounded':
      button.classList.add('rounded-lg')
      break
    case 'square':
      button.classList.add('rounded-none')
      break
    case 'pill':
      button.classList.add('rounded-full')
      break
  }
}

const editButtonText = (data) => {
  const { target, variables } = data
  if (!target) return

  // Remove any existing dialog first
  const existingDialog = document.querySelector(".button-text-dialog-container")
  if (existingDialog) {
    existingDialog.remove()
  }

  // Open the text edit dialog
  const container = document.createElement("div")
  container.className = "button-text-dialog-container"
  document.body.appendChild(container)

  // Get the current button text
  const buttonElement = target.classList.contains("editor-button") ? target : target.querySelector(".editor-button")
  const currentText = buttonElement ? buttonElement.textContent.trim() : ""

  const root = createRoot(container)

  root.render(
    <ButtonTextDialog
      defaultText={currentText}
      variables={variables}
      buttonElement={target}
      onClose={(result) => {
        if (result && result.text !== null) {
          runCommand("button.text", {
            selectedButton: target,
            text: result.text
          })
        }
        root.unmount()
        container.remove()
      }}
    />
  )

  // Return cleanup function
  return () => {
    root.unmount()
    container.remove()
  }
}

const editButtonStyle = (data) => {
  const { target } = data
  if (!target) return

  // Remove any existing dialog first
  const existingDialog = document.querySelector(".button-style-dialog-container")
  if (existingDialog) {
    existingDialog.remove()
  }

  // Open the style edit dialog
  const container = document.createElement("div")
  container.className = "button-style-dialog-container"
  document.body.appendChild(container)

  // Get the actual button element
  const buttonElement = target.classList.contains("editor-button") ? target : target.querySelector(".editor-button")

  const root = createRoot(container)

  root.render(
    <ButtonStyleDialog
      buttonElement={buttonElement}
      onClose={(result) => {
        root.unmount()
        container.remove()
      }}
    />
  )

  // Return cleanup function
  return () => {
    root.unmount()
    container.remove()
  }
}

const editButtonSource = (data) => {
  const { target } = data
  if (!target) return

  // Remove any existing dialog first
  const existingDialog = document.querySelector(".button-source-dialog-container")
  if (existingDialog) {
    existingDialog.remove()
  }

  // Open the source edit dialog
  const container = document.createElement("div")
  container.className = "button-source-dialog-container"
  document.body.appendChild(container)

  // Get the actual button element
  const buttonElement = target.classList.contains("editor-button") ? target : target.querySelector(".editor-button")

  const root = createRoot(container)

  root.render(
    <ButtonSourceDialog
      buttonElement={buttonElement}
      onClose={(result) => {
        root.unmount()
        container.remove()
      }}
    />
  )

  // Return cleanup function
  return () => {
    root.unmount()
    container.remove()
  }
}

const Button = ({ action, data }) => {
  switch (action) {
    case "add":
      return addButton(data)
    case "style":
      return updateButtonStyle(data)
    case "align":
      return updateButtonAlignment(data)
    case "text":
      return updateButtonText(data)
    case "text.edit":
      return editButtonText(data)
    case "style.edit":
      return editButtonStyle(data)
    case "source.edit":
      return editButtonSource(data)
    case "link":
      return updateButtonLink(data)
    case "delete":
      return deleteButton(data)
    case "duplicate":
      return duplicateButton(data)
    case "size":
      return updateButtonSize(data)
    case "variant":
      return updateButtonVariant(data)
    case "bgImage":
      return updateButtonBackground(data)
    case "removeBgImage":
      return removeButtonBackground(data)
    case "optionsMenu":
      return ButtonOptionsMenu(data)
    case "horizontalMenu":
      return HorizontalButtonOptionsMenu(data)
    case "transformGizmo":
      return ButtonTransformGizmo(data.selectedButton)
    default:
      console.warn(`Unknown button action: ${action}`)
      return null
  }
}

Button.config = DEFAULT_BUTTON_CONFIG
Button.utils = { createButtonElement, insertButton }

export default Button
